import type { Lang } from '@app/shared';

/**
 * Время на экране — В ПОЯСЕ ОРГАНИЗАЦИИ, никогда в поясе телефона (AGENTS.md, правило 1).
 *
 * Поэтому каждая функция здесь требует `tz` явным аргументом: параметр со значением
 * по умолчанию («если не передали — местный») однажды сработал бы, и клиент из Москвы
 * увидел бы 08:00 вместо ташкентских 10:00 — то есть опоздал бы на два часа.
 *
 * Метки слотов приходят с бэкенда готовыми (`Slot.label`) и здесь НЕ пересчитываются:
 * вторая реализация разошлась бы с той, по которой считаются напоминания.
 *
 * Календарная арифметика (следующие 14 дней) ведётся по строкам `YYYY-MM-DD`, а не
 * прибавлением 24 часов к моменту: сутки не всегда 24 часа, и в поясе с переводом стрелок
 * «завтра» уехало бы на день. Узбекистан стрелки не переводит, но пояс организации задаётся
 * полем `businesses.tz` и обязан работать любой.
 */

const LOCALES: Readonly<Record<Lang, string>> = { ru: 'ru-RU', uz: 'uz-UZ' };

function localeOf(lang: Lang): string {
  return LOCALES[lang];
}

/** Формат `YYYY-MM-DD` — тот же, что принимает `?date=` эндпоинта слотов. */
export type DayKey = string;

const DAY_KEY = /^(\d{4})-(\d{2})-(\d{2})$/;

export interface CalendarDay {
  readonly year: number;
  /** 1–12, как у человека, а не 0–11, как у `Date`. */
  readonly month: number;
  readonly day: number;
}

export function parseDayKey(key: DayKey): CalendarDay {
  const match = DAY_KEY.exec(key);
  if (!match) throw new Error(`Ожидался день в формате YYYY-MM-DD, получено: ${key}`);
  const [, year, month, day] = match;
  return { year: Number(year), month: Number(month), day: Number(day) };
}

function pad2(value: number): string {
  return String(value).padStart(2, '0');
}

function toDayKey(day: CalendarDay): DayKey {
  return `${String(day.year).padStart(4, '0')}-${pad2(day.month)}-${pad2(day.day)}`;
}

/** Календарный день момента в заданном поясе. */
export function dayKeyInTz(instant: Date, tz: string): DayKey {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(instant);

  const get = (type: Intl.DateTimeFormatPartTypes): string => {
    const part = parts.find((item) => item.type === type);
    if (!part) throw new Error(`Intl не вернул часть даты «${type}» для пояса ${tz}`);
    return part.value;
  };

  return `${get('year')}-${get('month')}-${get('day')}`;
}

/**
 * Сдвиг календарного дня. UTC-полдень как якорь: он не попадает ни в один перевод
 * стрелок и потому не даёт «пропущенных» и «удвоенных» суток.
 */
export function shiftDayKey(key: DayKey, days: number): DayKey {
  const { year, month, day } = parseDayKey(key);
  const anchor = new Date(Date.UTC(year, month - 1, day + days, 12));
  return toDayKey({
    year: anchor.getUTCFullYear(),
    month: anchor.getUTCMonth() + 1,
    day: anchor.getUTCDate(),
  });
}

/** Полоса дней для шага «3 · День»: `count` подряд идущих суток, начиная с `from`. */
export function dayKeyRange(from: DayKey, count: number): DayKey[] {
  if (!Number.isInteger(count) || count <= 0) {
    throw new Error(`Ожидалось положительное число дней, получено: ${count}`);
  }
  const days: DayKey[] = [];
  for (let index = 0; index < count; index += 1) days.push(shiftDayKey(from, index));
  return days;
}

/**
 * Подпись дня в полосе выбора: короткое имя дня недели и число.
 *
 * Считается в UTC по якорю полудня, а не в поясе организации: сам `DayKey` уже посчитан
 * в этом поясе, и второй пересчёт сместил бы подпись на сутки у дней, начинающихся
 * до полуночи UTC.
 */
export function dayKeyLabel(key: DayKey, lang: Lang): { weekday: string; day: string } {
  const { year, month, day } = parseDayKey(key);
  const anchor = new Date(Date.UTC(year, month - 1, day, 12));
  const weekday = new Intl.DateTimeFormat(localeOf(lang), {
    timeZone: 'UTC',
    weekday: 'short',
  }).format(anchor);
  return { weekday, day: String(day) };
}

function instantOf(iso: string): Date {
  const value = new Date(iso);
  if (Number.isNaN(value.getTime())) throw new Error(`Недопустимый момент времени: ${iso}`);
  return value;
}

/** «15:00» в поясе организации. Всегда 24 часа: в RU и UZ 12-часовой формат не используется. */
export function formatTimeInTz(instant: Date | string, tz: string, lang: Lang): string {
  return new Intl.DateTimeFormat(localeOf(lang), {
    timeZone: tz,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(typeof instant === 'string' ? instantOf(instant) : instant);
}

/** «12 ноября» / «12-noyabr». */
export function formatDateInTz(instant: Date | string, tz: string, lang: Lang): string {
  return new Intl.DateTimeFormat(localeOf(lang), {
    timeZone: tz,
    day: 'numeric',
    month: 'long',
  }).format(typeof instant === 'string' ? instantOf(instant) : instant);
}

/** «12 ноября, 15:00» — заголовок карточки записи. */
export function formatDateTimeInTz(instant: Date | string, tz: string, lang: Lang): string {
  const value = typeof instant === 'string' ? instantOf(instant) : instant;
  return `${formatDateInTz(value, tz, lang)}, ${formatTimeInTz(value, tz, lang)}`;
}

/** Цена: «80 000». Валюта дописывается ключом i18n `sum`, а не форматтером. */
export function formatAmount(value: number, lang: Lang): string {
  return new Intl.NumberFormat(localeOf(lang)).format(value);
}
